import { AnimatePresence, motion } from "motion/react";
import type { Reaction } from "../hooks/useCallUiState";

interface ReactionsOverlayProps {
  reactions: Reaction[];
}

/**
 * Derive a deterministic horizontal jitter (in pixels) from a reaction id so
 * the emoji's starting offset is stable across re-renders. Range: [-120, 120].
 */
function jitterFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  }
  return (h % 241) - 120;
}

/**
 * Floating emoji layer mounted above the video grid. Each reaction animates
 * upward and fades out over ~2.8s. Pointer events are disabled so the layer
 * never intercepts clicks on the controls or tiles beneath it.
 */
export function ReactionsOverlay({ reactions }: ReactionsOverlayProps) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-30">
      <AnimatePresence>
        {reactions.map((r) => {
          const jitter = jitterFor(r.id);
          return (
            <motion.div
              key={r.id}
              className="absolute bottom-24"
              style={{ left: `calc(50% + ${jitter}px)` }}
              initial={{ y: 0, opacity: 1, scale: 0.8 }}
              animate={{ y: -240, opacity: 0, scale: 1.4 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.8, ease: "easeOut" }}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-5xl">{r.emoji}</span>
                <span className="text-[11px] font-semibold text-white bg-black/50 rounded-full px-2 py-0.5 backdrop-blur-sm whitespace-nowrap">
                  {r.senderName}
                </span>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export default ReactionsOverlay;
